/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { useEffect, useState } from 'react';

import { REMEMBER_CREDENTIALS_KEY } from './remember-credentials-key';

/**
 * @returns {[boolean, Function]}
 */
export function useRememberCredentials({ selectedClusterLSKey, clientId, setClientId, clientSecret, setClientSecret }) {
  const [rememberCredentials, setRememberCredentials] = useState(false);

  useRetrieveCredentialsFromLSOnLoadAndClusterChange({
    selectedClusterLSKey,
    setRememberCredentials,
    setClientId,
    setClientSecret
  });

  usePersistCredentialsToLSOnValueChange({
    selectedClusterLSKey,
    clientId,
    clientSecret,
    rememberCredentials
  });

  return [rememberCredentials, setRememberCredentials];
}

const getCredentialsFromLS = (selectedClusterLSKey) => {
  const allCredentials = JSON.parse(localStorage.getItem(REMEMBER_CREDENTIALS_KEY)) || {};
  const credentialsForSelectedCluster = allCredentials[selectedClusterLSKey];

  return { allCredentials, credentialsForSelectedCluster };
};

const useRetrieveCredentialsFromLSOnLoadAndClusterChange = ({
  selectedClusterLSKey,
  setRememberCredentials,
  setClientId,
  setClientSecret
}) => {
  useEffect(() => {
    const { credentialsForSelectedCluster } = getCredentialsFromLS(selectedClusterLSKey);

    if (credentialsForSelectedCluster?.rememberCredentials) {
      setRememberCredentials(credentialsForSelectedCluster.rememberCredentials);
      setClientId(credentialsForSelectedCluster.clientId);
      setClientSecret(credentialsForSelectedCluster.clientSecret);
    } else {
      setRememberCredentials(false);
      setClientId('');
      setClientSecret('');
    }
  }, [selectedClusterLSKey, setClientId, setClientSecret, setRememberCredentials]);
};

const usePersistCredentialsToLSOnValueChange = ({
  selectedClusterLSKey,
  clientId,
  clientSecret,
  rememberCredentials
}) => {
  useEffect(() => {
    const { allCredentials, credentialsForSelectedCluster } = getCredentialsFromLS(selectedClusterLSKey);

    if (credentialsForSelectedCluster) {
      credentialsForSelectedCluster.rememberCredentials = rememberCredentials;
      if (rememberCredentials) {
        credentialsForSelectedCluster.clientId = clientId;
        credentialsForSelectedCluster.clientSecret = clientSecret;
      } else {
        credentialsForSelectedCluster.clientId = '';
        credentialsForSelectedCluster.clientSecret = '';
      }

      localStorage.setItem(REMEMBER_CREDENTIALS_KEY, JSON.stringify(allCredentials));
    }
  }, [clientId, clientSecret, rememberCredentials, selectedClusterLSKey]);
};
